import { useLazyQuery } from "@apollo/client";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { Modal, Tooltip } from "@mui/material";
import axios from "axios";
import PropTypes from "prop-types";
import { FC, useRef, useState } from "react";

import { getCroppedImg } from "./canvasHelper";
import ImageCropper from "./ImageCropper";
import {
  CloseButton,
  ErrorText,
  HeaderContainer,
  ModalBox,
  PrimaryButton,
  SecondaryButton,
  TitleSection,
  TitleSectionHead,
  TitleSectionPara,
  UploadBox,
  UploadFooter,
  UploadInfo,
  UploadSection,
} from "./ImageUploadModal.styles";
import {
  ImageProperties,
  ImageUploadProps,
  MAX_FILE_SIZE,
  UPLOAD_PROFILE_PHOTO_SIGNED_URL_QUERY,
  UploadProfilePhotoSignedUrlResponse,
  VALID_IMAGE_TYPES,
} from "./ImageUploadModal.types";

const ImageUploadModal: FC<ImageUploadProps> = ({
  accountId,
  handleClose,
  handleSubmit,
  openModal,
}) => {
  const [dragEnter, setDragEnter] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const uploadProfilePicInputRef = useRef<HTMLInputElement>(null);
  const [imageProperties, setImageProperties] = useState<ImageProperties>({
    zoom: 1,
    rotation: 0,
    croppedAreaPixels: { height: 0, width: 0, x: 0, y: 0 },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [getSignedUrl, { loading: getSignedUrlLoading }] =
    useLazyQuery<UploadProfilePhotoSignedUrlResponse>(UPLOAD_PROFILE_PHOTO_SIGNED_URL_QUERY, {
      fetchPolicy: "network-only",
    });

  const handleDragEvent = (event: React.MouseEvent, isEnter = true) => {
    setDragEnter(isEnter);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    setDragEnter(false);
    validateFile(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    validateFile(file);
  };

  const attachButtonHandler = () => {
    uploadProfilePicInputRef.current?.click();
  };

  const cancelUpload = () => {
    setErrorMsg("");
    setSelectedImage(null);
  };

  const validateFile = (file: File) => {
    if (file) {
      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        return setErrorMsg("Please upload a .jpeg/.jpg or .png file type");
      }
      if (file.size > MAX_FILE_SIZE) {
        return setErrorMsg("Image file size should be less than 10MB");
      }

      setErrorMsg("");
      const reader = new FileReader();
      reader.onload = () => setSelectedImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUploadSubmit = async () => {
    setIsLoading(true);
    const { rotation, croppedAreaPixels } = imageProperties;
    if (!selectedImage) {
      return setErrorMsg("No image selected. Please upload an image.");
    }

    const croppedImage = await getCroppedImg(selectedImage, croppedAreaPixels, rotation);
    if (!croppedImage) {
      return setErrorMsg("Failed to crop the image. Please try again.");
    }

    try {
      const { data: signedUrlData } = await getSignedUrl({
        variables: { accountId },
      });
      if (!signedUrlData?.uploadProfilePhotoSignedUrl) {
        return setErrorMsg("Could not retrieve upload URL.");
      }

      const signedUrl = signedUrlData.uploadProfilePhotoSignedUrl;
      const { status } = await axios.put(signedUrl, croppedImage, {
        headers: { "Content-Type": croppedImage.type },
      });
      if (status === 200) {
        await handleSubmit();
        setIsLoading(false);
        setErrorMsg("");
        setSelectedImage(null);
        handleClose();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("An error occurred while preparing the photo upload.", e);
      setErrorMsg("An error occurred while preparing the photo upload.");
      setIsLoading(false);
    }
  };

  const loading = getSignedUrlLoading || isLoading;

  const handleChangeImage = () => {
    cancelUpload();
    uploadProfilePicInputRef.current?.click();
  };

  if (!openModal) {
    return null;
  }

  return (
    <Modal open={openModal} onClose={handleClose} aria-labelledby="image-upload-modal">
      <ModalBox>
        <HeaderContainer>
          <TitleSection>
            <TitleSectionHead>Upload Photo</TitleSectionHead>
            <TitleSectionPara>
              Upload a photo of the patient to personalize their account
            </TitleSectionPara>
          </TitleSection>
          <Tooltip title="Close">
            <CloseButton onClick={handleClose}>
              <CancelRoundedIcon />
            </CloseButton>
          </Tooltip>
        </HeaderContainer>

        <UploadSection>
          {selectedImage ? (
            <ImageCropper uploadedImage={selectedImage} setImageProperties={setImageProperties} />
          ) : (
            <>
              <UploadBox
                htmlFor="fileInput"
                $dragEnter={dragEnter}
                onDragEnter={handleDragEvent}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onDragLeave={(e) => handleDragEvent(e, false)}
              >
                <input
                  type="file"
                  id="fileInput"
                  accept="image/*"
                  onChange={handleFileChange}
                  hidden
                  ref={uploadProfilePicInputRef}
                />
                {dragEnter ? `Drop the file here` : `Attach or Drag & Drop your photo here`}
              </UploadBox>
              <UploadInfo variant="body2">200 x 200 min / 5MB max</UploadInfo>
            </>
          )}
          {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
        </UploadSection>

        <UploadFooter>
          {selectedImage === null ? (
            <>
              <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
              <PrimaryButton onClick={attachButtonHandler}>Attach Photo</PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={handleChangeImage}>Change Photo</SecondaryButton>
              <PrimaryButton onClick={handlePhotoUploadSubmit} disabled={loading}>
                {loading ? "Uploading..." : "Upload Photo"}
              </PrimaryButton>
            </>
          )}
        </UploadFooter>
      </ModalBox>
    </Modal>
  );
};

ImageUploadModal.propTypes = {
  accountId: PropTypes.number.isRequired,
  openModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ImageUploadModal;
